import { useContext, useEffect, useRef } from 'react';

import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import type { AttachmentActionSurface } from '../../types.ts';

import {
	AttachmentActionDiscoveryExperienceCoordinator,
	type AttachmentActionDiscoveryExperienceLease,
	type AttachmentActionDiscoverySuccessReason,
} from './AttachmentActionDiscoveryExperienceCoordinator.ts';

export type AttachmentActionDiscoveryExperienceStatus =
	| 'access-unavailable'
	| 'failure'
	| 'loading'
	| 'success';

const coordinator = new AttachmentActionDiscoveryExperienceCoordinator();

const settleLease = (
	lease: AttachmentActionDiscoveryExperienceLease,
	status: Exclude<AttachmentActionDiscoveryExperienceStatus, 'loading'>,
	successReason?: AttachmentActionDiscoverySuccessReason,
): void => {
	if (status === 'success') {
		lease.succeed(successReason);
	} else if (status === 'failure') {
		lease.fail();
	} else {
		lease.abort('access-unavailable');
	}
};

export const useAttachmentActionDiscoveryExperience = ({
	discoveryKey,
	enabled,
	participantId,
	status,
	successReason,
	surface,
}: {
	discoveryKey: string | null;
	enabled: boolean;
	participantId: string;
	status: AttachmentActionDiscoveryExperienceStatus;
	successReason?: AttachmentActionDiscoverySuccessReason;
	surface: AttachmentActionSurface;
}): void => {
	const tracker = useContext(ExperienceTrackerContext);
	const leaseRef = useRef<AttachmentActionDiscoveryExperienceLease | null>(null);
	const latestOutcomeRef = useRef({ status, successReason });
	latestOutcomeRef.current = { status, successReason };

	useEffect(() => {
		if (!enabled || !discoveryKey) {
			return;
		}

		const registeredLease = coordinator.register({ participantId, surface, tracker });
		leaseRef.current = registeredLease;
		const latestOutcome = latestOutcomeRef.current;
		if (latestOutcome.status !== 'loading') {
			settleLease(registeredLease, latestOutcome.status, latestOutcome.successReason);
		}

		return () => {
			registeredLease.abort('unmounted-before-settled');
			if (leaseRef.current === registeredLease) {
				leaseRef.current = null;
			}
		};
	}, [discoveryKey, enabled, participantId, surface, tracker]);

	useEffect(() => {
		const lease = leaseRef.current;
		if (!enabled || !discoveryKey || !lease || status === 'loading') {
			return;
		}

		settleLease(lease, status, successReason);
	}, [discoveryKey, enabled, status, successReason]);
};
